import type { CongressCardProps } from "../ui/CongressCard";
import { cn } from "../../lib/utils";

type CongressCover = Extract<
  CongressCardProps["display"],
  { variant: "full" }
>["cover"];

/**
 * The congress visual on desktop: the client's artwork as the organiser made it,
 * like a collection thumbnail. Nothing of ours is drawn over an artwork (it
 * already carries the name and the dates, see `docs/congress-space.md`). A
 * colour cover shows its logo, or the colour alone.
 *
 * The caller sizes it: the same visual is a banner tile, a page header and a
 * one-line strip thumbnail.
 */
export function DesktopCongressCover({
  cover,
  coverColor,
  name,
  className,
}: {
  cover: CongressCover;
  coverColor: string;
  name: string;
  className: string;
}): React.JSX.Element {
  switch (cover.type) {
    case "artwork":
      return (
        <img
          src={cover.artworkUrl}
          alt={name}
          className={cn("shrink-0 object-cover", className)}
        />
      );
    case "backdrop":
      return (
        <img
          src={cover.backdropUrl}
          alt={name}
          className={cn("shrink-0 object-cover", className)}
        />
      );
    case "color":
      return (
        <div
          role="img"
          aria-label={name}
          className={cn("flex shrink-0 items-center justify-center", className)}
          style={{ backgroundColor: coverColor }}
        >
          {cover.logoUrl === null ? null : (
            <img
              src={cover.logoUrl}
              alt=""
              className="max-h-1/2 max-w-2/3 object-contain"
            />
          )}
        </div>
      );
    default:
      throw new Error(cover satisfies never);
  }
}
